<script setup lang="ts">
import type { UseQueryOptions, UseQuerySharedOptions } from '@core/types'
import type { RewardTypes } from '@promotion/types'
import type { defineAsyncComponent } from '#imports'
import { computed, toValue, vInternalLinks, useI18n, useCoreConfig, useRoute, usePromotionTermsQueries, usePromotionClient, useStrapiConsent, useQuery, navigateTo, useThemeLogo } from '#imports'
import type { UIDialog } from '#components'

defineProps<{
    dynamicTermsComponents?: Partial<Record<RewardTypes, ReturnType<typeof defineAsyncComponent>>>
}>()

const { t, te } = useI18n()
const { site } = useCoreConfig()
const headerLogo = useThemeLogo('icon')
const route = useRoute()
const { client } = usePromotionClient()
const { getGeneralTerms, getPrivacyPolicy } = useStrapiConsent()
const {
    generalTerms,
    privacyPolicy,
    bonusCode,
    promotionReferenceId,
    bonusReferenceId,
    excludedGames,
    bingoGames,
    isPromotionTerms,
    isActive,
    key,
    endpointUrl,
    monolithTermsEnabled,
    promotionTermsEnabled,
    excludedGamesTermsEnabled,
    bingoGamesTermsEnabled,
    promotionReferenceTermsEnabled,
    bonusReferenceTermsEnabled,
    generalTermsEnabled,
    privacyPolicyEnabled,
} = usePromotionTermsQueries(site.brand)

const headerText = computed(() => {
    if (!isActive.value) return

    if (privacyPolicy.value) {
        return t('promotions.termsDialog.headers.privacyPolicy')
    }

    if (bonusCode.value) {
        return t('promotions.termsDialog.headers.bonusTerms')
    }

    if (excludedGames.value) {
        return t('promotions.termsDialog.headers.excludedGames')
    }

    if (bingoGames.value) {
        return t('promotions.termsDialog.headers.bingoGames')
    }

    if (isPromotionTerms.value) {
        return t('promotions.termsDialog.headers.promotionTerms')
    }

    return t('promotions.termsDialog.headers.generalTerms')
})

const referenceTermsKey = computed(() => promotionReferenceId.value ? `promotion:promotion-terms-${promotionReferenceId.value}` : 'promotion:reference-terms-inactive')
const bonusReferenceTermsKey = computed(() => bonusReferenceId.value ? `promotion:bonus-terms-${bonusReferenceId.value}` : 'promotion:bonus-reference-terms-inactive')

function getPromotionReferenceTerms(options: Pick<UseQuerySharedOptions, 'enabled'> & Pick<UseQueryOptions<any>, 'lazy' | 'server'> = {}) {
    const { enabled = true, lazy = true, server = false } = options

    const query = useQuery(async () => {
        const id = toValue(promotionReferenceId)

        if (!id) return null

        const { data } = await client.GET('/public/promotions/{promotionReferenceId}/full-terms', {
            params: {
                path: {
                    promotionReferenceId: id as string
                }
            }
        })

        return data
    }, {
        key: referenceTermsKey,
        lazy,
        server,
        enabled,
    })

    return query
}

function getBonusReferenceTerms(options: Pick<UseQuerySharedOptions, 'enabled'> & Pick<UseQueryOptions<any>, 'lazy' | 'server'> = {}) {
    const { enabled = true, lazy = true, server = false } = options

    const query = useQuery(async () => {
        const id = toValue(bonusReferenceId)

        if (!id) return null

        const { data } = await client.GET('/public/bonuses/{bonusReferenceId}/full-terms', {
            params: {
                path: {
                    bonusReferenceId: id as string
                }
            }
        })

        return data
    }, {
        key: bonusReferenceTermsKey,
        lazy,
        server,
        enabled,
    })

    return query
}

const bonusReferenceTermsQuery = await getBonusReferenceTerms({
    enabled: bonusReferenceTermsEnabled,
})

const promotionReferenceTermsQuery = await getPromotionReferenceTerms({
    enabled: promotionReferenceTermsEnabled,
})

const promotionTermsQuery = await useQuery<any>(endpointUrl, {
    key,
    enabled: monolithTermsEnabled,
    lazy: true,
    server: false,
    service: 'monolith'
})

const generalTermsQuery = await getGeneralTerms({
    enabled: generalTermsEnabled,
    lazy: true,
    server: false
})

const privacyPolicyQuery = await getPrivacyPolicy({
    enabled: privacyPolicyEnabled,
    lazy: true,
    server: false
})

const error = computed(() => {
    if (bonusReferenceId.value && bonusReferenceTermsQuery.error.value)
        return t('promotions.pageNotFound')

    if (promotionReferenceId.value && promotionReferenceTermsQuery.error.value)
        return t('promotions.pageNotFound')

    if (monolithTermsEnabled.value && promotionTermsQuery.error.value)
        return t('promotions.pageNotFound')

    if (generalTerms.value && generalTermsQuery.error.value)
        return t('promotions.pageNotFound')

    if (privacyPolicy.value && privacyPolicyQuery.error.value)
        return t('promotions.pageNotFound')

    return false
})

const promotionTermsHtml = computed(() => {
    if (promotionTermsQuery.data?.value?.excluded_games) {
        return gamesListHtml(promotionTermsQuery.data?.value?.excluded_games, {
            className: 'excluded-games-list',
            afterTextKey: 'promotions.excludedGames.afterGames'
        })
    }

    if (promotionTermsQuery.data?.value?.bingo_games) {
        return gamesListHtml(promotionTermsQuery.data?.value?.bingo_games, {
            className: 'bingo-games-list'
        })
    }

    return promotionTermsQuery.data?.value?.terms_html
        || promotionTermsQuery.data?.value?.long_terms
        || promotionTermsQuery.data?.value?.html
        || promotionTermsQuery.data?.value?.terms?.html
})

const gamesListHtml = (gamesArray: string[] | string, options: { className: string, afterTextKey?: string }) => {
    const { className, afterTextKey } = options
    const afterTextHtml = afterTextKey && te(afterTextKey) ? `<p>${t(afterTextKey)}</p>` : ''

    if (!Array.isArray(gamesArray)) {
        return `${gamesArray}${afterTextHtml}`
    }

    return `<ul class="${className}">${gamesArray.map(game => `<li>${game}</li>`).join('')}</ul>${afterTextHtml}`
}

async function onModalClose() {
    await navigateTo({
        query: {
            ...route.query,
            'bonusCode': undefined,
            'bundleId': undefined,
            'promotionId': undefined,
            'terms': undefined,
            'general-terms': undefined,
            'privacy-policy': undefined,
            'excluded-games': undefined,
            'bingo-games': undefined,
            'referenceId': undefined,
            'promotionReferenceId': undefined,
            'bonusReferenceId': undefined,
        }
    })
}
</script>

<template>
    <UIDialog
        ref="dialog"
        :model-value="true"
        :show-close-icon="true"
        :scrollable="true"
        :wide="true"
        :header="headerText"
        :header-logo-src="headerLogo?.src"
        min-height="300px"
        :backdrop-props="{
            style: {
                'z-index': 18 // We use 1 higher z-index than dialogs to make sure the terms dialog is on top.
            }
        }"
        @close="onModalClose()"
    >
        <LazyUIAlert v-if="error" :message="error" />

        <template v-else-if="excludedGamesTermsEnabled && promotionTermsQuery.data.value">
            <!-- eslint-disable vue/no-v-html -->
            <div
                v-if="promotionTermsHtml"
                v-internal-links
                class="terms--html"
                v-html="promotionTermsHtml"
            />
            <!-- eslint-enable vue/no-v-html -->
        </template>

        <template v-else-if="bingoGamesTermsEnabled && promotionTermsQuery.data.value">
            <!-- eslint-disable vue/no-v-html -->
            <div
                v-if="promotionTermsHtml"
                v-internal-links
                class="terms--html"
                v-html="promotionTermsHtml"
            />
            <!-- eslint-enable vue/no-v-html -->
        </template>

        <template v-else-if="bonusReferenceId && bonusReferenceTermsQuery.data.value?.full_terms">
            <!-- eslint-disable vue/no-v-html -->
            <div
                v-internal-links
                class="terms--html"
                v-html="bonusReferenceTermsQuery.data.value.full_terms"
            />
            <!-- eslint-enable vue/no-v-html -->
        </template>

        <template v-else-if="promotionReferenceId && promotionReferenceTermsQuery.data.value?.full_terms">
            <!-- eslint-disable vue/no-v-html -->
            <div
                v-internal-links
                class="terms--html"
                v-html="promotionReferenceTermsQuery.data.value.full_terms"
            />
            <!-- eslint-enable vue/no-v-html -->
        </template>

        <template v-else-if="promotionTermsEnabled && promotionTermsQuery.data.value">
            <!-- eslint-disable vue/no-v-html -->
            <div
                v-if="promotionTermsHtml"
                v-internal-links
                class="terms--html"
                v-html="promotionTermsHtml"
            />
            <!-- eslint-enable vue/no-v-html -->

            <div
                v-else-if="promotionTermsQuery.data.value?.terms"
                v-internal-links
            >
                <LazyPromotionDynamicTerms
                    class="terms--html"
                    :dynamic-terms-components="dynamicTermsComponents"
                    :terms-data="promotionTermsQuery.data.value.terms"
                />
            </div>
        </template>

        <div v-else-if="generalTerms && generalTermsQuery?.data?.value" class="terms--html">
            <NimbusGeneralTerms :content="generalTermsQuery?.data?.value" />
        </div>

        <div v-else-if="privacyPolicy && privacyPolicyQuery.data.value" class="terms--html">
            <NimbusStrapiBlockRenderer :content="privacyPolicyQuery.data.value.content" />
        </div>

        <LazyUILoader v-else :text="t('promotions.fetchingTerms')" />
    </UIDialog>
</template>

<style lang="scss">
.terms--html {
    margin: auto; // Center short messages
    padding-bottom: 80px; // Make it able to see the bottom text always on mobile
    color: inherit;

    @media (min-width: $screen-md) {
        padding-bottom: 30px;
    }

    h1,
    h2,
    h3 {
        font-size: $font-size-xl;
    }

    h2:first-child {
        margin-bottom: 20px;
    }

    ul,
    ol {
        padding-left: 2px;

        li {
            margin-bottom: 10px;
        }
    }

    ul {
        margin: 15px 0;
        padding-left: 17px;
    }

    dt {
        width: auto;
        float: left;
        margin-right: 10px;
    }

    dd {
        margin-bottom: 10px;
        overflow: hidden;
    }

    table {
        width: 100%;
        font-size: $font-size-xs;

        th,
        td {
            padding: 5px;
            border: 1px solid;

            @media (min-width: $screen-md) {
                padding: 10px;
            }
        }

        tr {
            vertical-align: top;
        }
    }

    .excluded-games-list,
    .bingo-games-list {
        columns: 100px;
        -webkit-column-break-inside: avoid;
        page-break-inside: avoid;
        break-inside: avoid;
        list-style: none;
        padding: 0;

        @include tablet {
            columns: 150px;
        }

        @include desktop {
            columns: 200px;
        }

        li {
            margin: 0;
            padding: 5px 0;
            line-height: 1.1;
        }

        li:nth-child(even) {
            color: var(--on-info-soft, $brand-info-text);
        }
    }
}
</style>
